import React, { useState, useEffect } from 'react';
import './EmployeeForm.css';

const emptyLoanRow = () => ({
  id: null,
  principalAmount: '',
  termMonths: '',
  startYear: '',
  startMonth: '',
  label: '',
});

const loanRowIsEmptyRaw = (loan) =>
  !String(loan?.principalAmount ?? '').trim() &&
  !String(loan?.termMonths ?? '').trim() &&
  !String(loan?.startYear ?? '').trim() &&
  !String(loan?.startMonth ?? '').trim();

const EmployeeForm = ({ employee, isEditing, onSubmit, onClose }) => {
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    dateNaissance: '',
    email: '',
    telephone: '',
    adresse: '',
    cin: '',
    cnssNumber: '',
    matricule: '',
    baseTH: '',
    maritalStatus: 'SINGLE',
    children: 0,
    qualification: '',
    categorie: '',
    echellon: '',
    dateEmbauche: '',
    typeContrat: 'CDI',
    cadre: false,
    overtimePaid: true,
    statutActif: true,
    loans: [],
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [promotionInfo, setPromotionInfo] = useState(null);


  // Obtenir les options d'échelon (1 à 15)
  // Note : Les catégories (1-16) et les échelons (1-15) sont maintenant indépendants
  const getEchellonOptions = () => {
    // Toujours retourner tous les échelons de 1 à 15, indépendamment de la catégorie
    return Array.from({ length: 15 }, (_, i) => i + 1);
  };

  useEffect(() => {
    if (employee) {
      setFormData({
        nom: employee.nom || '',
        prenom: employee.prenom || '',
        dateNaissance: employee.dateNaissance || '',
        email: employee.email || '',
        telephone: employee.telephone || '',
        adresse: employee.adresse || '',
        cin: employee.cin || '',
        cnssNumber: employee.cnssNumber || '',
        matricule: employee.matricule || '',
        baseTH: employee.baseTH != null ? employee.baseTH.toString() : '',
        maritalStatus: employee.maritalStatus || 'SINGLE',
        children: employee.children ?? 0,
        qualification: employee.qualification || '',
        // Note : Les catégories peuvent être numériques (1-16) ou textuelles (OUVRIER/MAITRISE/CADRE)
        // Les catégories et échelons sont indépendants
        categorie: employee.categorie || '',
        echellon: employee.echellon || '',
        dateEmbauche: employee.dateEmbauche || '',
        typeContrat: employee.typeContrat || 'CDI',
        cadre: employee.cadre ?? false,
        statutActif: employee.statutActif !== undefined ? employee.statutActif : true,
        overtimePaid: employee.overtimePaid !== undefined ? employee.overtimePaid : true,
        loans:
          employee.loans && employee.loans.length > 0
            ? employee.loans.map((l) => ({
                id: l.id ?? null,
                principalAmount:
                  l.principalAmount != null ? String(l.principalAmount) : '',
                termMonths: l.termMonths != null ? String(l.termMonths) : '',
                startYear: l.startYear != null ? String(l.startYear) : '',
                startMonth: l.startMonth != null ? String(l.startMonth) : '',
                label: l.label || '',
              }))
            : [],
      });
    }
  }, [employee]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^[0-9+\-\s()]+$/;
    return phoneRegex.test(phone) && phone.length >= 8 && phone.length <= 20;
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.nom.trim()) {
      newErrors.nom = 'Le nom est obligatoire';
    } else if (formData.nom.length < 2 || formData.nom.length > 50) {
      newErrors.nom = 'Le nom doit contenir entre 2 et 50 caractères';
    }

    if (!formData.prenom.trim()) {
      newErrors.prenom = 'Le prénom est obligatoire';
    } else if (formData.prenom.length < 2 || formData.prenom.length > 50) {
      newErrors.prenom = 'Le prénom doit contenir entre 2 et 50 caractères';
    }

    if (!formData.dateNaissance) {
      newErrors.dateNaissance = 'La date de naissance est obligatoire';
    } else {
      const birthDate = new Date(formData.dateNaissance);
      const today = new Date();
      if (birthDate >= today) {
        newErrors.dateNaissance = 'La date de naissance doit être dans le passé';
      }
    }

    if (formData.email && formData.email.trim() && !validateEmail(formData.email)) {
      newErrors.email = "L'email doit être valide";
    }

    if (!formData.telephone.trim()) {
      newErrors.telephone = 'Le téléphone est obligatoire';
    } else if (!validatePhone(formData.telephone)) {
      newErrors.telephone = 'Le format du téléphone est invalide (8-20 caractères)';
    }

    if (!formData.cin.trim()) {
      newErrors.cin = 'Le CIN est obligatoire';
    } else if (!/^[0-9]{8}$/.test(formData.cin)) {
      newErrors.cin = 'Le CIN doit contenir exactement 8 chiffres';
    }

    if (!formData.cnssNumber.trim()) {
      newErrors.cnssNumber = 'Le numéro CNSS est obligatoire';
    } else if (!/^[0-9]{8,13}$/.test(formData.cnssNumber)) {
      newErrors.cnssNumber = 'Le numéro CNSS doit contenir entre 8 et 13 chiffres';
    }

    if (!formData.maritalStatus) {
      newErrors.maritalStatus = 'La situation familiale est obligatoire';
    }

    if (formData.children < 0) {
      newErrors.children = 'Le nombre d\'enfants ne peut pas être négatif';
    }

    if (!formData.qualification.trim()) {
      newErrors.qualification = 'La qualification est obligatoire';
    } else if (formData.qualification.length < 2 || formData.qualification.length > 100) {
      newErrors.qualification = 'La qualification doit contenir entre 2 et 100 caractères';
    }

    // Validation de la date d'embauche si elle est fournie (optionnelle)
    if (formData.dateEmbauche) {
      const hireDate = new Date(formData.dateEmbauche);
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      if (hireDate > today) {
        newErrors.dateEmbauche = 'La date d\'embauche ne peut pas être dans le futur';
      }
    }

    const allowedContracts = ['CDI', 'CDD', 'STAGE', 'FREELANCE', 'SIVP', 'KARAMA', 'GERANT'];
    if (!formData.typeContrat) {
      newErrors.typeContrat = 'Le type de contrat est obligatoire';
    } else if (!allowedContracts.includes(formData.typeContrat)) {
      newErrors.typeContrat = 'Le type de contrat doit être: CDI, CDD, STAGE, FREELANCE, SIVP, KARAMA ou GERANT';
    }

    (formData.loans || []).forEach((loan, i) => {
      if (loanRowIsEmptyRaw(loan)) return;
      const p =
        loan.principalAmount && String(loan.principalAmount).trim() !== ''
          ? parseFloat(loan.principalAmount)
          : null;
      const t =
        loan.termMonths !== '' && loan.termMonths != null
          ? parseInt(loan.termMonths, 10)
          : null;
      const y =
        loan.startYear !== '' && loan.startYear != null
          ? parseInt(loan.startYear, 10)
          : null;
      const m =
        loan.startMonth !== '' && loan.startMonth != null
          ? parseInt(loan.startMonth, 10)
          : null;
      const parts = [];
      if (p == null || Number.isNaN(p) || p <= 0) {
        parts.push('montant total > 0');
      }
      if (t == null || Number.isNaN(t) || t < 1) {
        parts.push('mensualités ≥ 1');
      }
      if (y == null || Number.isNaN(y)) {
        parts.push('année de début');
      }
      if (m == null || Number.isNaN(m) || m < 1 || m > 12) {
        parts.push('mois 1–12');
      }
      if (parts.length > 0) {
        newErrors[`loan_${i}`] = `Prêt ${i + 1} : ${parts.join(', ')}`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue = type === 'checkbox' ? checked : value;
    if (name === 'children') {
      const parsed = parseInt(newValue || 0, 10);
      newValue = Number.isNaN(parsed) ? 0 : Math.max(0, parsed);
    } else if (name === 'baseTH') {
      // Gérer baseTH comme nombre décimal
      if (value === '' || value === null || value === undefined) {
        newValue = '';
      } else {
        const parsed = parseFloat(value);
        newValue = Number.isNaN(parsed) ? '' : (parsed < 0 ? '' : value);
      }
    } else if (name === 'categorie') {
      // Stocker la catégorie (peut être numérique 1-16 ou textuelle OUVRIER/MAITRISE/CADRE)
      // Note : La catégorie et l'échelon sont maintenant indépendants
      const upper = (value || '').toUpperCase();
      newValue = upper;
      // Ne pas réinitialiser l'échelon car ils sont indépendants
      setFormData(prev => ({
        ...prev,
        categorie: newValue
      }));
      // Nettoyer seulement l'erreur sur categorie
      setErrors(prev => {
        const updated = { ...prev };
        delete updated.categorie;
        return updated;
      });
      return;
    }
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const updateLoan = (index, field, value) => {
    setFormData((prev) => {
      const next = [...(prev.loans || [])];
      next[index] = { ...next[index], [field]: value };
      return { ...prev, loans: next };
    });
    setErrors((prev) => {
      const u = { ...prev };
      delete u[`loan_${index}`];
      return u;
    });
  };

  const addLoanRow = () => {
    setFormData((prev) => ({
      ...prev,
      loans: [...(prev.loans || []), emptyLoanRow()],
    }));
  };

  const removeLoanRow = (index) => {
    setFormData((prev) => ({
      ...prev,
      loans: (prev.loans || []).filter((_, j) => j !== index),
    }));
    setErrors((prev) => {
      const u = { ...prev };
      delete u[`loan_${index}`];
      return u;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const loansPayload = (formData.loans || [])
        .filter((loan) => !loanRowIsEmptyRaw(loan))
        .map((loan) => ({
          id: loan.id || undefined,
          principalAmount: parseFloat(loan.principalAmount),
          termMonths: parseInt(loan.termMonths, 10),
          startYear: parseInt(loan.startYear, 10),
          startMonth: parseInt(loan.startMonth, 10),
          label: loan.label && String(loan.label).trim() ? String(loan.label).trim() : null,
        }));

      const dataToSubmit = {
        ...formData,
        baseTH: formData.baseTH && formData.baseTH.trim() !== ''
          ? parseFloat(formData.baseTH)
          : null,
        matricule: formData.matricule && formData.matricule.trim() !== ''
          ? formData.matricule.trim()
          : null,
        loans: loansPayload,
      };
      
      await onSubmit(dataToSubmit);
    } catch (error) {
      // Error handling is done in parent component
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEditing ? 'Modifier l\'employé' : 'Ajouter un employé'}</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="employee-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="nom">Nom *</label>
              <input
                type="text"
                id="nom"
                name="nom"
                value={formData.nom}
                onChange={handleChange}
                className={errors.nom ? 'error' : ''}
              />
              {errors.nom && <span className="error-message">{errors.nom}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="prenom">Prénom *</label>
              <input
                type="text"
                id="prenom"
                name="prenom"
                value={formData.prenom}
                onChange={handleChange}
                className={errors.prenom ? 'error' : ''}
              />
              {errors.prenom && <span className="error-message">{errors.prenom}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="dateNaissance">Date de naissance *</label>
              <input
                type="date"
                id="dateNaissance"
                name="dateNaissance"
                value={formData.dateNaissance}
                onChange={handleChange}
                className={errors.dateNaissance ? 'error' : ''}
              />
              {errors.dateNaissance && <span className="error-message">{errors.dateNaissance}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="dateEmbauche">Date d'embauche</label>
              <input
                type="date"
                id="dateEmbauche"
                name="dateEmbauche"
                value={formData.dateEmbauche}
                onChange={handleChange}
                className={errors.dateEmbauche ? 'error' : ''}
                max={new Date().toISOString().split('T')[0]}
              />
              {errors.dateEmbauche && <span className="error-message">{errors.dateEmbauche}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={errors.email ? 'error' : ''}
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="telephone">Téléphone *</label>
              <input
                type="tel"
                id="telephone"
                name="telephone"
                value={formData.telephone}
                onChange={handleChange}
                className={errors.telephone ? 'error' : ''}
                placeholder="+33 6 12 34 56 78"
              />
              {errors.telephone && <span className="error-message">{errors.telephone}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="adresse">Adresse</label>
              <input
                type="text"
                id="adresse"
                name="adresse"
                value={formData.adresse}
                onChange={handleChange}
                placeholder="Adresse complète..."
                maxLength={255}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="qualification">Qualification *</label>
              <input
                type="text"
                id="qualification"
                name="qualification"
                value={formData.qualification}
                onChange={handleChange}
                className={errors.qualification ? 'error' : ''}
              />
              {errors.qualification && <span className="error-message">{errors.qualification}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="typeContrat">Type de contrat *</label>
              <select
                id="typeContrat"
                name="typeContrat"
                value={formData.typeContrat}
                onChange={handleChange}
                className={errors.typeContrat ? 'error' : ''}
              >
                <option value="CDI">CDI</option>
                <option value="CDD">CDD</option>
                <option value="STAGE">STAGE</option>
                <option value="FREELANCE">FREELANCE</option>
                <option value="SIVP">SIVP</option>
                <option value="KARAMA">KARAMA</option>
                <option value="GERANT">GÉRANT</option>
              </select>
              {errors.typeContrat && <span className="error-message">{errors.typeContrat}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="categorie">
                Catégorie
                <span className="tooltip-icon" title="Catégorie professionnelle de l'employé (1-16). Les catégories peuvent être numériques (1-16) ou textuelles (OUVRIER, MAITRISE, CADRE). Les catégories et échelons sont indépendants.">ℹ️</span>
              </label>
              <input
                type="text"
                id="categorie"
                name="categorie"
                value={formData.categorie}
                onChange={handleChange}
                placeholder="1-16 ou OUVRIER/MAITRISE/CADRE"
                className={errors.categorie ? 'error' : ''}
              />
              {errors.categorie && <span className="error-message">{errors.categorie}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="echellon">
                Échelon
                <span className="tooltip-icon" title="Échelon de l'employé (1-15). Les catégories (1-16) et les échelons (1-15) sont indépendants - chaque catégorie peut avoir n'importe quel échelon de 1 à 15.">ℹ️</span>
              </label>
              <select
                id="echellon"
                name="echellon"
                value={formData.echellon}
                onChange={handleChange}
              >
                <option value="">Sélectionner</option>
                {getEchellonOptions().map(e => (
                  <option key={e} value={String(e)}>{e}</option>
                ))}
              </select>
            </div>
            <div className="form-group checkbox-group">
              <label htmlFor="cadre">Statut</label>
              <div className="checkbox-inline">
                <input
                  type="checkbox"
                  id="cadre"
                  name="cadre"
                  checked={!!formData.cadre}
                  onChange={handleChange}
                />
                <span>Cadre</span>
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group checkbox-group">
              <label htmlFor="overtimePaid">Heures supplémentaires</label>
              <div className="checkbox-inline">
                <input
                  type="checkbox"
                  id="overtimePaid"
                  name="overtimePaid"
                  checked={!!formData.overtimePaid}
                  onChange={handleChange}
                />
                <span>Heures supplémentaires payées en fiche de paie</span>
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="cin">CIN *</label>
              <input
                type="text"
                id="cin"
                name="cin"
                value={formData.cin}
                onChange={handleChange}
                className={errors.cin ? 'error' : ''}
                placeholder="12345678"
              />
              {errors.cin && <span className="error-message">{errors.cin}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="cnssNumber">Numéro CNSS *</label>
              <input
                type="text"
                id="cnssNumber"
                name="cnssNumber"
                value={formData.cnssNumber}
                onChange={handleChange}
                className={errors.cnssNumber ? 'error' : ''}
                placeholder="0123456789012"
              />
              {errors.cnssNumber && <span className="error-message">{errors.cnssNumber}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="matricule">Matricule</label>
              <input
                type="text"
                id="matricule"
                name="matricule"
                value={formData.matricule}
                onChange={handleChange}
                className={errors.matricule ? 'error' : ''}
                placeholder="Ex: 715"
                maxLength={50}
              />
              {errors.matricule && <span className="error-message">{errors.matricule}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="baseTH">Salaire de base</label>
              <input
                type="number"
                id="baseTH"
                name="baseTH"
                value={formData.baseTH}
                onChange={handleChange}
                className={errors.baseTH ? 'error' : ''}
                placeholder="0.000"
                step="0.001"
                min="0"
              />
              {errors.baseTH && <span className="error-message">{errors.baseTH}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="maritalStatus">Situation familiale *</label>
              <select
                id="maritalStatus"
                name="maritalStatus"
                value={formData.maritalStatus}
                onChange={handleChange}
                className={errors.maritalStatus ? 'error' : ''}
              >
                <option value="SINGLE">Célibataire</option>
                <option value="MARRIED">Marié(e)</option>
              </select>
              {errors.maritalStatus && <span className="error-message">{errors.maritalStatus}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="children">Nombre d'enfants *</label>
              <input
                type="number"
                id="children"
                name="children"
                min="0"
                value={formData.children}
                onChange={handleChange}
                className={errors.children ? 'error' : ''}
              />
              {errors.children && <span className="error-message">{errors.children}</span>}
            </div>
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                name="statutActif"
                checked={formData.statutActif}
                onChange={handleChange}
              />
              <span>Statut actif</span>
            </label>
          </div>

          <h3 className="employee-form-section-title">Prêts / retenues sur salaire</h3>
          <p className="employee-form-hint">
            Ajoutez une ou plusieurs lignes de prêt. Chaque ligne préremplit le champ « Crédit » sur la fiche de paie
            pour les mois concernés (mensualité = montant ÷ nombre de mois ; le dernier mois ajuste les arrondis).
            Si plusieurs prêts sont actifs le même mois, les montants sont additionnés.
          </p>
          {(formData.loans || []).map((loan, i) => (
            <div key={i} className="employee-loan-card">
              <div className="employee-loan-card-header">
                <strong>Prêt {i + 1}</strong>
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={() => removeLoanRow(i)}
                >
                  Retirer
                </button>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Libellé (optionnel)</label>
                  <input
                    type="text"
                    value={loan.label}
                    onChange={(e) => updateLoan(i, 'label', e.target.value)}
                    placeholder="Ex. Prêt équipement"
                    maxLength={100}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Montant total (TND)</label>
                  <input
                    type="number"
                    value={loan.principalAmount}
                    onChange={(e) => updateLoan(i, 'principalAmount', e.target.value)}
                    step="0.001"
                    min="0"
                    className={errors[`loan_${i}`] ? 'error' : ''}
                  />
                </div>
                <div className="form-group">
                  <label>Nombre de mensualités</label>
                  <input
                    type="number"
                    value={loan.termMonths}
                    onChange={(e) => updateLoan(i, 'termMonths', e.target.value)}
                    min="1"
                    placeholder="Ex. 12"
                    className={errors[`loan_${i}`] ? 'error' : ''}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Début des retenues — année</label>
                  <select
                    value={loan.startYear}
                    onChange={(e) => updateLoan(i, 'startYear', e.target.value)}
                    className={errors[`loan_${i}`] ? 'error' : ''}
                  >
                    <option value="">—</option>
                    {Array.from({ length: 16 }, (_, j) => new Date().getFullYear() - 5 + j).map((y) => (
                      <option key={y} value={String(y)}>{y}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Mois (1–12)</label>
                  <select
                    value={loan.startMonth}
                    onChange={(e) => updateLoan(i, 'startMonth', e.target.value)}
                    className={errors[`loan_${i}`] ? 'error' : ''}
                  >
                    <option value="">—</option>
                    {Array.from({ length: 12 }, (_, j) => j + 1).map((m) => (
                      <option key={m} value={String(m)}>{m}</option>
                    ))}
                  </select>
                </div>
              </div>
              {errors[`loan_${i}`] && (
                <span className="error-message">{errors[`loan_${i}`]}</span>
              )}
            </div>
          ))}
          <div className="form-group" style={{ marginBottom: '1rem' }}>
            <button type="button" className="btn btn-outline" onClick={addLoanRow}>
              + Ajouter un prêt
            </button>
          </div>

          <div className="form-actions">
            <button type="button" onClick={onClose} className="btn btn-secondary">
              Annuler
            </button>
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Enregistrement...' : (isEditing ? 'Modifier' : 'Ajouter')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmployeeForm;

